import { useState } from "react";
import { Checkbox } from "antd";
import { AppModal, Input } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { submitPatientSsnApi } from "@/portals/patient/api/patientProfileApi";
import { SSN_COPY } from "./ssnConsentCopy";

interface SsnModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the server's ssnProvidedAt after a successful save. */
  onSaved: (ssnProvidedAt: string) => void;
}

function formatGroups(value: string) {
  const parts = [value.slice(0, 3), value.slice(3, 5), value.slice(5, 9)].filter(Boolean);
  return parts.join("-");
}

function maskedDisplay(digits: string) {
  if (digits.length === 9) return `•••-••-${digits.slice(5)}`;
  return formatGroups("•".repeat(digits.length));
}

/**
 * Optional SSN entry. How the number is handled here:
 * - It lives only in this component's state, only until the save request
 *   finishes (success or failure), then it's cleared. Never in
 *   localStorage, sessionStorage, router state, or a URL.
 * - Once the field loses focus it shows only the last 4 (•••-••-1234).
 *   Focusing it again clears it, so the full number is never shown back.
 */
export function SsnModal({ open, onClose, onSaved }: SsnModalProps) {
  const [digits, setDigits] = useState("");
  const [focused, setFocused] = useState(false);
  const [needsReentry, setNeedsReentry] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setDigits("");
    setFocused(false);
    setNeedsReentry(false);
    setConsent(false);
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFocus = () => {
    if (digits) {
      setDigits("");
      setNeedsReentry(true);
    }
    setFocused(true);
  };

  const handleSubmit = async () => {
    if (digits.length !== 9 || !consent || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: apiError, retryAfterSeconds } = await submitPatientSsnApi(digits, consent);
      if (data) {
        reset();
        onSaved(data.ssnProvidedAt);
        onClose();
        return;
      }
      setError(
        retryAfterSeconds
          ? `${apiError ?? "Too many attempts."} You can try again in about ${Math.ceil(retryAfterSeconds / 60)} minute(s).`
          : apiError ?? "Something went wrong. Please try again.",
      );
    } finally {
      // Success or failure, the number doesn't outlive the request.
      setDigits("");
      setNeedsReentry(false);
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={open}
      onOpenChange={(next) => { if (!next) close(); }}
      width={480}
      className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!p-8"
    >
      <h2 className="text-lg font-extrabold text-[#0A2947]">{SSN_COPY.title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">{SSN_COPY.why}</p>
      <p className="mt-2 text-sm font-semibold leading-relaxed text-[#374151]">{SSN_COPY.notRequired}</p>

      <label htmlFor="ssn-match-input" className="mt-5 mb-1.5 block text-xs font-semibold text-[#374151]">
        Social Security Number
      </label>
      <Input
        id="ssn-match-input"
        // Not "ssn": keeps browsers from offering to save/autofill it.
        name="identity-match-number"
        autoComplete="off"
        inputMode="numeric"
        spellCheck={false}
        placeholder="•••-••-••••"
        value={focused ? formatGroups(digits) : maskedDisplay(digits)}
        onFocus={handleFocus}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setDigits(e.target.value.replace(/\D/g, "").slice(0, 9));
          setError(null);
        }}
        disabled={submitting}
        aria-describedby={needsReentry ? "ssn-reenter-hint" : undefined}
      />
      {needsReentry && (
        <p id="ssn-reenter-hint" className="mt-1 text-xs text-[#6B7280]">{SSN_COPY.reenterHint}</p>
      )}

      <Checkbox
        className="mt-4 [&_.ant-checkbox-inner]:!border-[#E4002B]/50 [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-[#E4002B]/50 [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-[#E4002B]/50"
        checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={submitting}>
        <span className="text-xs text-[#374151]">
          {SSN_COPY.consentLabel}{" "}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#E4002B]">
            {SSN_COPY.privacyLinkLabel}
          </a>
          .
        </span>
      </Checkbox>

      {error && <p className="mt-3 text-xs font-semibold text-[#E4002B]" role="alert">{error}</p>}

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={close} disabled={submitting}>
          {SSN_COPY.skip}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={digits.length !== 9 || !consent}
          busy={submitting}
          className="!border-none !bg-[#E4002B]/50 !text-white hover:!bg-[#E4002B]/60 disabled:!opacity-50"
        >
          {SSN_COPY.submit}
        </Button>
      </div>
    </AppModal>
  );
}
